// DEBT-VITA™ — Toggle "Inclure l'analyse DEBT-VITA dans le rapport global ?"
// Quand activé, les sections dette sont ajoutées au rapport principal lors de
// la prochaine génération (PDF / Word). Préférence stockée dans localStorage
// par projet, lue par les générateurs principaux (à brancher en DV-finalisation).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, ExternalLink, FileDown, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getDebtParameters, getLatestDebtResult } from "@/lib/debt";

const STORAGE_KEY = (pid) => `vtpDebtVitaInclusion:${pid}`;

export default function DebtVitaInclusionToggle({ projectId, project }) {
  const [enabled, setEnabled] = useState(false);
  const [hasParams, setHasParams] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const stored = localStorage.getItem(STORAGE_KEY(projectId));
        if (stored !== null) setEnabled(stored === "1");
        const [p, r] = await Promise.all([
          getDebtParameters(projectId).catch(() => null),
          getLatestDebtResult(projectId).catch(() => null),
        ]);
        if (cancelled) return;
        setHasParams(!!p);
        setHasResults(!!r);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Debt inclusion check failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  function handleToggle(v) {
    if (v && !hasResults) {
      toast.error(
        "Lancez d'abord un calcul DEBT-VITA™ avant de l'inclure au rapport global."
      );
      return;
    }
    setEnabled(v);
    localStorage.setItem(STORAGE_KEY(projectId), v ? "1" : "0");
    toast.success(
      v
        ? "L'analyse DEBT-VITA™ sera incluse dans la prochaine génération."
        : "L'analyse DEBT-VITA™ ne sera pas incluse dans le rapport global."
    );
  }

  return (
    <Card
      className="border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/15"
      data-testid="debt-vita-inclusion-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Coins className="h-5 w-5 text-amber-700 dark:text-amber-400" />
          Inclure l'analyse DEBT-VITA™ dans le rapport global ?
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasParams || !hasResults ? (
          <div className="flex flex-wrap items-start gap-3" data-testid="debt-vita-inclusion-cta">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
            <div className="flex-1 text-sm">
              <p>
                {!hasParams
                  ? "Aucun paramètre DEBT-VITA™ n'a été saisi pour ce projet."
                  : "Aucun calcul DEBT-VITA™ n'a encore été lancé."}
              </p>
              <p className="mt-1 text-muted-foreground">
                Pour inclure l'analyse de la dette dans votre rapport,
                configurez d'abord les paramètres et lancez le calcul depuis le
                module dédié.
              </p>
              <Link
                to={`/app/projects/${projectId}/debt`}
                className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-amber-900 underline hover:no-underline dark:text-amber-200"
                data-testid="debt-vita-inclusion-open-module"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Ouvrir DEBT-VITA™
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id="debt-vita-toggle"
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={loading}
                data-testid="debt-vita-inclusion-toggle"
              />
              <Label
                htmlFor="debt-vita-toggle"
                className="text-sm font-semibold"
              >
                {enabled ? "Activé" : "Désactivé"}
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to={`/app/projects/${projectId}/debt`}
                className="text-xs text-muted-foreground underline hover:text-foreground"
                data-testid="debt-vita-inclusion-open-dashboard"
              >
                <ExternalLink className="mr-1 inline h-3 w-3" />
                Dashboard DEBT-VITA™
              </Link>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  // Best-effort : open DEBT-VITA™ directly to its own report dropdown
                  window.location.href = `/app/projects/${projectId}/debt`;
                }}
                data-testid="debt-vita-inclusion-direct-report"
              >
                <FileDown className="mr-1 h-3.5 w-3.5" />
                Générer le rapport DEBT-VITA™ dédié
              </Button>
            </div>
          </div>
        )}

        {enabled ? (
          <p className="mt-3 rounded-md border border-emerald-300/50 bg-emerald-50 p-2 text-[11px] text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200">
            ✓ Préférence enregistrée. Lors de la prochaine génération PDF/Word
            globale, une section dédiée DEBT-VITA™ (KPIs + niveau d'alerte
            vitaliste) sera ajoutée. Le rapport DEBT-VITA™ <em>complet</em>
            (12 pages thème sombre) reste disponible séparément depuis le module.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
